import PropTypes from 'prop-types'
import { Button } from 'antd'
import { CalendarOutlined, FileSearchOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import dayjs from '../../lib/datetime'
import { toPath } from '../../routes'
import PatientEntry from '../Common/PatientEntry'

export const EXPAND_COLUMN_WIDTH = 40

const useStyles = createUseStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  name: {
    fontWeight: 600,
    fontSize: 15,
    minWidth: 0,
    overflowWrap: 'anywhere',
    '& a': {
      whiteSpace: 'normal !important',
    },
  },
  line: {
    fontSize: 13,
    color: '#595959',
    overflowWrap: 'anywhere',
  },
  lineIcon: {
    marginRight: 6,
    color: '#6b6b6b',
  },
  // Lets a field take the full width of the 2-column details grid.
  spanTwo: {
    gridColumn: '1 / -1',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 16px',
    paddingLeft: EXPAND_COLUMN_WIDTH,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 11,
    color: '#6b6b6b',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  fieldValue: {
    fontSize: 14,
    color: '#1c1c1c',
    overflowWrap: 'anywhere',
  },
})

function formatDate(date) {
  return date ? dayjs(date).format('L') : '—'
}

function formatTransmissionDate(date) {
  return date ? dayjs(date).format('MM/DD/YYYY HH:mm:ss') : '—'
}

function patientDateOfBirth(patient = {}) {
  return patient.dateOfBirth || patient.birthDate
}

function patientMrn(patient = {}) {
  return patient.mrn || patient.practiceMrn
}

function Field({ label, children = null, span = false }) {
  const classes = useStyles()
  return (
    <div
      className={span ? `${classes.field} ${classes.spanTwo}` : classes.field}
    >
      <span className={classes.fieldLabel}>{label}</span>
      <span className={classes.fieldValue}>{children}</span>
    </div>
  )
}

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  span: PropTypes.bool,
}

export function TransmissionMobileCard({ transmission }) {
  const classes = useStyles()
  const { device, patient, transmissionDate, isReviewed } = transmission
  // Status is always present, so this line never renders empty.
  const deviceLine = [device?.type, isReviewed ? 'Reviewed' : 'Unreviewed']
    .filter(Boolean)
    .join(' · ')

  return (
    <div className={classes.card}>
      <div className={classes.name}>
        {patient ? (
          <PatientEntry patient={patient} name={patient.fullName} />
        ) : (
          'Unmatched transmission'
        )}
      </div>
      <div className={classes.line}>
        <CalendarOutlined className={classes.lineIcon} />
        {formatTransmissionDate(transmissionDate)}
      </div>
      <div className={classes.line}>{deviceLine}</div>
    </div>
  )
}

TransmissionMobileCard.propTypes = {
  transmission: PropTypes.shape({
    transmissionDate: PropTypes.string,
    isReviewed: PropTypes.bool,
    patient: PropTypes.shape({
      fullName: PropTypes.string,
    }),
    device: PropTypes.shape({
      type: PropTypes.string,
    }),
  }).isRequired,
}

export function TransmissionMobileDetails({ transmission, returnTo }) {
  const classes = useStyles()
  const { device, id, patient, practice } = transmission

  return (
    <div className={classes.detailsGrid}>
      <Field label="MRN">{patientMrn(patient) || '—'}</Field>
      <Field label="DOB">{formatDate(patientDateOfBirth(patient))}</Field>
      <Field label="Device type">{device?.type || '—'}</Field>
      <Field label="Serial number">{device?.serialNumber || '—'}</Field>
      <Field label="Practice" span>
        {practice?.name || '—'}
      </Field>
      <div className={classes.spanTwo}>
        <Link
          to={toPath.transmissions.transmission({ id })}
          state={{ returnTo }}
        >
          <Button
            type="link"
            size="small"
            icon={<FileSearchOutlined />}
            style={{ paddingLeft: 0 }}
          >
            Open transmission
          </Button>
        </Link>
      </div>
    </div>
  )
}

TransmissionMobileDetails.propTypes = {
  returnTo: PropTypes.string.isRequired,
  transmission: PropTypes.shape({
    id: PropTypes.string.isRequired,
    patient: PropTypes.shape({}),
    practice: PropTypes.shape({
      name: PropTypes.string,
    }),
    device: PropTypes.shape({
      type: PropTypes.string,
      serialNumber: PropTypes.string,
    }),
  }).isRequired,
}
